import { useId } from 'react';
import { AXIS_COLOR, AXIS_INK, TRACK_COLOR } from './tokens';
import { linearScale, zeroDomain } from './scale';

// =========================================================================
// BarChart — the demo's single-series bar (source line 1976). Fork-owned.
//
// Horizontal, one bar per row, no axis furniture beyond a baseline: this is
// the "rank these by size" chart, not the "read the exact value off the axis"
// chart. The exact value is printed at the end of every bar, so the axis has
// nothing left to do.
//
// A row can carry its own colour token — that is how the same component draws
// a ledger ranking (one accent) and a condition distribution (five band
// tokens) without a variant prop.
// =========================================================================

/** One bar. */
export interface BarRow {
  readonly label: string;
  readonly value: number;
  /** Colour token for this bar. Defaults to the brand accent. */
  readonly color?: string;
  /** Replaces the formatted value at the end of the bar. */
  readonly valueLabel?: string;
  /** Secondary line under the label. */
  readonly detail?: string;
}

export interface BarChartProps {
  rows: readonly BarRow[];
  format: (value: number) => string;
  /** Width of the label gutter, in viewBox units. */
  gutter?: number;
  rowHeight?: number;
  /** Accessible name. */
  title: string;
  /** Draws the full-width track behind each bar. */
  showTrack?: boolean;
  /**
   * viewBox width, in user units — see the note on `StackedBar`. Roughly 560
   * for a half column, 1120 for a full one, so text is the same size in both.
   */
  viewWidth?: number;
}

const DEFAULT_VIEW_WIDTH = 560;
const PAD_RIGHT = 74;
const PAD_TOP = 6;
const PAD_BOTTOM = 8;

export function BarChart({
  rows,
  format,
  gutter = 132,
  rowHeight = 28,
  title,
  showTrack = false,
  viewWidth = DEFAULT_VIEW_WIDTH,
}: BarChartProps) {
  const titleId = useId();
  const plotLeft = gutter;
  const plotRight = viewWidth - PAD_RIGHT;
  const height = PAD_TOP + rows.length * rowHeight + PAD_BOTTOM;

  const domain = zeroDomain(rows.map((r) => r.value));
  const x = linearScale(domain, { min: plotLeft, max: plotRight });

  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm" style={{ color: 'var(--ink-tertiary)' }}>
        No rows to chart.
      </p>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${viewWidth} ${height}`}
      className="w-full"
      style={{ height: 'auto', overflow: 'visible' }}
      role="img"
      aria-labelledby={titleId}
      preserveAspectRatio="xMidYMid meet"
    >
      <title id={titleId}>
        {title}. {rows.length} bars, largest {format(domain.max)}.
      </title>

      {rows.map((row, i) => {
        const y = PAD_TOP + i * rowHeight;
        const barY = y + rowHeight * 0.26;
        const barH = rowHeight * 0.44;
        const w = Math.max(1, x(row.value) - x(0));
        return (
          <g key={`${row.label}-${i}`}>
            <text
              x={plotLeft - 10}
              y={y + rowHeight * 0.5 + (row.detail ? 0 : 4)}
              textAnchor="end"
              fontSize={11.5}
              fill="var(--ink-secondary)"
            >
              {row.label}
            </text>
            {row.detail ? (
              <text
                x={plotLeft - 10}
                y={y + rowHeight * 0.5 + 11}
                textAnchor="end"
                fontSize={9.5}
                fill={AXIS_INK}
              >
                {row.detail}
              </text>
            ) : null}

            {showTrack ? (
              <rect
                x={plotLeft}
                y={barY}
                width={plotRight - plotLeft}
                height={barH}
                fill={TRACK_COLOR}
                rx={2}
              />
            ) : null}

            <rect
              x={plotLeft}
              y={barY}
              width={w}
              height={barH}
              fill={row.color ?? 'var(--accent)'}
              rx={2}
            >
              <title>{`${row.label} · ${row.valueLabel ?? format(row.value)}`}</title>
            </rect>

            <text
              x={plotRight + 8}
              y={y + rowHeight * 0.5 + 4}
              fontSize={11.5}
              fill="var(--ink-primary)"
              className="mono"
            >
              {row.valueLabel ?? format(row.value)}
            </text>
          </g>
        );
      })}

      <line
        x1={plotLeft}
        x2={plotLeft}
        y1={PAD_TOP}
        y2={PAD_TOP + rows.length * rowHeight}
        stroke={AXIS_COLOR}
        strokeWidth={1}
      />
    </svg>
  );
}
